/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-lines */

import { showErrorAlert } from 'actions';
import { createIdentifier, setShowEditIdentifier } from 'actions/identifier';
import GlobalNavbar from 'components/Navbar/GlobalNavbar';
import PageHeader from 'components/PageHeader';
import SelectRedesign from 'components/Select/SelectRedesign';
import TermsInput from 'components/TermsInput';
import {
  deploymentOptions,
  getDefaultPromptBlocks,
  kindOptions,
  sourceOptions,
} from 'constants/entities';
import useNavParams from 'hooks/nav/useNavParams';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'utils/urls';
import { v4 } from 'uuid';
import PrompSectionCard, { LLMBlock } from './PrompSectionCard';

const LlmCategorizerName: React.FC = () => {
  const defaultBlocks = getDefaultPromptBlocks();

  const history = useHistory();
  const dispatch = useDispatch();
  const { channel_id: channelId, name: nameParam } = useNavParams() as {
    channel_id: string;
    name: string;
  };

  const [name, setName] = useState(nameParam || '');
  const [llmIdentifierType, setLlmIdentifierType] = useState('');
  const [deploy, setDeploy] = useState('');
  const [source, setSource] = useState('');
  const [categories, setCategories] = useState<string[]>([]);

  const [blocks, setBlocks] = useState<LLMBlock[]>(defaultBlocks);
  const [expandedBlock, setExpandedBlock] = useState(defaultBlocks[0].id);

  useEffect(() => {
    dispatch(setShowEditIdentifier(false));
  }, [dispatch]);

  const handleSubmit = (): void => {
    if (!name) {
      dispatch(showErrorAlert('Missing Prompt Name'));
      return;
    }
    if (!llmIdentifierType) {
      dispatch(showErrorAlert('Missing Type'));
      return;
    }
    if (!deploy) {
      dispatch(showErrorAlert('Missing Deploy'));
      return;
    }
    if (!source) {
      dispatch(showErrorAlert('Missing Source'));
      return;
    }

    dispatch(
      createIdentifier({
        name,
        description: '',
        kind: 'llm_categorizer',
        options_config: {
          // @ts-ignore
          categorization_facets: categories,
          model: deploy,
          content_source: source,
          type: llmIdentifierType,
          variables: [],
        },
        // @ts-ignore
        language_matchers: blocks.map((block) => ({
          uuid: block.id,
          name: block.name,
          text: '',
          placeholder: block.pattern,
          type: 'text',
          ...(block.parent ? { parent: block.parent } : {}),
        })),
        channelId,
        redirect: true,
      })
    );
  };

  const handleCancel = (): void => {
    if (channelId) {
      window.close();
      return;
    }
    history.goBack();
  };

  const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setName(e.target.value);
  };

  const handleChangeLlmIdentifierType = (value: string): void => {
    setLlmIdentifierType(value);
  };

  const handleChangeDeploy = (value: string): void => {
    setDeploy(value);
  };

  const handleChangeSource = (value: string): void => {
    setSource(value);
  };
  const handleAddCategory = (value: string): void => {
    if (!value) return;
    if (categories.includes(value)) return;
    setCategories((cats) => [...cats, value]);
  };
  const handleDeleteCategories = (value: string): void => {
    setCategories((cats) => cats.filter((c) => c !== value));
  };

  const handleSetExpandedBlock = (block: LLMBlock, collapsed: boolean): void => {
    if (collapsed) setExpandedBlock('');
    setExpandedBlock(block.id);
  };

  const handleAddBlock = (): void => {
    const id = v4();
    setBlocks((blks) => [...blks, { id, name: 'New Block', pattern: '', type: 'text' }]);
    setExpandedBlock(id);
  };

  const handleDeleteBlock = (block: LLMBlock): void => {
    setBlocks((blks) => blks.filter((b) => b.id !== block.id));
  };

  const handleChangeBlock = (block: LLMBlock): void => {
    setBlocks((blks) => blks.map((b) => (b.id === block.id ? block : b)));
  };

  const handleMoveBlock = (block: LLMBlock, direction: 'up' | 'down'): void => {
    const temp = [...blocks];
    const fromIndex = blocks.findIndex((b) => b.id === block.id);

    if (
      fromIndex === -1 ||
      (fromIndex === 0 && direction === 'up') ||
      (fromIndex === blocks.length - 1 && direction === 'down')
    ) {
      return;
    }

    if (block.parent) {
      const siblings = blocks.filter((b) => b.parent === block.parent);
      if (siblings.length <= 1) {
        return;
      }
      const childIndex = siblings.findIndex((b) => b.id === block.id);
      if (
        (childIndex === 0 && direction === 'up') ||
        (childIndex === siblings.length - 1 && direction === 'down')
      ) {
        return;
      }
    }

    let howManyToMove =
      blocks.reduce((acc, val) => (val.parent && val.parent === block.id ? acc + 1 : acc), 0) + 1;
    const spliced = temp.splice(fromIndex, howManyToMove);

    const directionStep = direction === 'up' ? -1 : 1;
    let toIndex = direction === 'up' ? fromIndex + directionStep : fromIndex;
    const toBlock = temp[toIndex];
    const toBlockParentId = toBlock.parent ? toBlock.parent : toBlock.id;

    let howManyMoreSpacesToMove = 0;
    if (blocks.some((b) => b.parent && b.parent === toBlockParentId)) {
      howManyMoreSpacesToMove = blocks.reduce(
        (acc, val) => (val.parent && val.parent === toBlockParentId ? acc + 1 : acc),
        0
      );
    }

    if (block.parent) {
      howManyToMove = 1;
      howManyMoreSpacesToMove = 0;
    }

    toIndex +=
      direction === 'up'
        ? directionStep * howManyMoreSpacesToMove
        : directionStep * howManyMoreSpacesToMove + 1;
    temp.splice(toIndex, 0, ...spliced);

    setBlocks(temp);
  };

  return (
    <div className="min-h-screen min-w-screen bg-white text-body">
      <GlobalNavbar />
      <div className="w-full flex flex-col pt-6 pb-10">
        <div className="flex flex-col gap-6">
          <header className="px-10 flex flex-col gap-8">
            <PageHeader />
            <div className="flex flex-row justify-between">
              <h2 className="text-heading-1">LLM Categorizer</h2>

              <div className="flex flex-row gap-2">
                <button
                  type="button"
                  data-testid="go-back-button"
                  onClick={(): void => handleCancel()}
                  className="button button--secondary h-8 w-37 justify-center"
                >
                  <span className="font-bold">Cancel</span>
                </button>
                <button
                  type="button"
                  data-testid="create-button"
                  onClick={handleSubmit}
                  className="button button--primary h-8 w-37 justify-center"
                >
                  Create
                </button>
              </div>
            </div>
          </header>

          <main className="flex flex-col gap-4">
            <div className="px-10 flex flex-col gap-6">
              <div className="flex flex-col gap-2">
                <label htmlFor="feature" className="flex flex-col gap-1 text-body w-1/2">
                  <div className="flex flex-row">
                    <span>Prompt name</span>
                    <span className="text-litlingo-alert">*</span>
                  </div>
                  <input
                    name="feature"
                    id="feature"
                    type="text"
                    data-testid="matcher-feature-input"
                    value={name}
                    className="form-input w-124 h-9 py-2 px-4 border border-litlingo-gray-2 rounded-lg text-body focus:outline-none"
                    onChange={handleChangeName}
                    pattern=".*\S.*"
                  />
                </label>
                <div className="flex flex-row gap-4">
                  <div className="w-50 flex flex-col gap-1 text-body">
                    <div className="flex flex-row">
                      <span>Type</span>
                      <span className="text-litlingo-alert">*</span>
                    </div>
                    <SelectRedesign
                      onChange={handleChangeLlmIdentifierType}
                      options={kindOptions}
                      value={llmIdentifierType}
                    />
                  </div>

                  <div className="w-50 flex flex-col gap-1 text-body">
                    <div className="flex flex-row">
                      <span>Deploy with</span>
                      <span className="text-litlingo-alert">*</span>
                    </div>
                    <SelectRedesign
                      onChange={handleChangeDeploy}
                      options={deploymentOptions}
                      value={deploy}
                    />
                  </div>

                  <div className="w-50 flex flex-col gap-1 text-body">
                    <div className="flex flex-row">
                      <span>Source</span>
                      <span className="text-litlingo-alert">*</span>
                    </div>
                    <SelectRedesign
                      onChange={handleChangeSource}
                      options={sourceOptions}
                      value={source}
                    />
                  </div>
                </div>
              </div>
              <div className="w-78">
                <TermsInput
                  inputName="Categories"
                  onChange={handleAddCategory}
                  onDelete={handleDeleteCategories}
                  terms={categories}
                  required={false}
                />
              </div>
            </div>
            <div className="border-b border-litlingo-gray-2" />
            <div className="px-10 w-full">
              <div className="w-164 flex flex-col gap-4">
                <div className="w-full flex flex-col gap-2">
                  <h3 className="w-full text-heading-3">Structure your Parent Prompt</h3>
                  <p className="w-full">
                    Select to move, add, or edit. Edits only apply to the pattern input’s helper
                    text here and will affect the all Parent Prompt template.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  {blocks.map((block) => (
                    <>
                      <PrompSectionCard
                        key={block.id}
                        block={block}
                        handleChangeBlock={handleChangeBlock}
                        handleDeleteBlock={handleDeleteBlock}
                        handleMoveBlock={handleMoveBlock}
                        handleSelectExpandedBlock={handleSetExpandedBlock}
                        isCollapsed={block.id !== expandedBlock}
                      />
                    </>
                  ))}
                  <button
                    type="button"
                    className="w-full h-12 button button--secondary justify-center"
                    onClick={handleAddBlock}
                  >
                    <span className="font-bold">+ Add new section</span>
                  </button>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default LlmCategorizerName;
